/**
 * What a person may do, decided in one place. Features ask `can(account, feature)`; nothing else knows about plans.
 * To change who gets what (a Pro plan, a trial) edit PLANS below, not the features.
 *
 * Plans: a guest has no account; a signed-in account is `free` (for now: everything the app has); `pro` is reserved.
 *
 * EARLY ACCESS: guest limits are OFF, so a guest gets everything a signed-in account does (see `guestLimitsOn`). Signing in is
 * optional and only links a person's computers. The limits and locked features below stay in PLANS, ready to switch back on with
 * `setGuestLimits(true)` (the web app does it for a build made with VITE_GUEST_LIMITS=1; tests of the limits do it directly).
 */
export type Plan = "guest" | "free" | "pro";

export type Feature =
  /** Answer Claude/Codex approval requests and questions from the phone, and the full Decision Inbox. */
  | "inbox.decisions"
  /** Continue chats that were started elsewhere (open in a terminal, or from the agent's own history). */
  | "sessions.import"
  /** Earlier messages of a chat, and the list of past conversations in a folder. */
  | "history.conversation"
  /** Source control: changes, branches, graph. */
  | "git";

export interface Limits {
  /** Computers one phone may connect. */
  maxComputers: number;
  /** Sessions running at once on a computer. */
  maxActiveSessions: number;
  /** Sessions that may be started in 24 hours on a computer (a guest's trial). */
  maxSessionsPerDay: number;
}

export interface Entitlements {
  plan: Plan;
  features: ReadonlySet<Feature>;
  limits: Limits;
}

const ALL: Feature[] = ["inbox.decisions", "sessions.import", "history.conversation", "git"];
const UNLIMITED: Limits = { maxComputers: Infinity, maxActiveSessions: Infinity, maxSessionsPerDay: Infinity };

const PLANS: Record<Plan, { features: Feature[]; limits: Limits }> = {
  guest: { features: ["inbox.decisions"], limits: { maxComputers: 1, maxActiveSessions: 1, maxSessionsPerDay: Infinity } },
  free: { features: ALL, limits: UNLIMITED },
  pro: { features: ALL, limits: UNLIMITED },
};

let guestLimits = true;
/** Are a guest's limits (one computer, one running session, locked git/history/import) enforced? On. */
export const guestLimitsOn = (): boolean => guestLimits;
export function setGuestLimits(on: boolean): void {
  guestLimits = on;
}

/** Anything with a plan, or nothing at all (a guest). */
export type AccountLike = { plan?: Plan } | null | undefined;

export function planOf(account: AccountLike): Plan {
  return account ? (account.plan ?? "free") : "guest";
}

export function getEntitlements(account: AccountLike): Entitlements {
  const plan = planOf(account);
  const p = plan === "guest" && !guestLimits ? PLANS.free : PLANS[plan]; // early access: a guest is not held back
  return { plan, features: new Set(p.features), limits: p.limits };
}

export function can(account: AccountLike, feature: Feature): boolean {
  return getEntitlements(account).features.has(feature);
}

/** What to tell a guest who hits a locked feature (the same words everywhere). */
export const UPGRADE_COPY: Record<Feature, string> = {
  "inbox.decisions": "Sign in to approve and answer Claude and Codex from your phone.",
  "sessions.import": "Sign in to continue chats that started in your terminal or in the agent's history.",
  "history.conversation": "Sign in to read earlier messages and past conversations.",
  git: "Sign in to use source control: changes, branches and history.",
};
export const UPGRADE_TITLE = "Create an account to continue";
export const UPGRADE_SESSIONS = "Sign in to run more than one session at a time.";
export const UPGRADE_COMPUTERS = "Sign in to connect more than one computer.";
